<template>
  <div class="stories-page">
    <CultureNav />
    <CultureHero
      title="Stories Along the Way"
      eyebrow="The Reckon Way"
      summary="The journeys show where we went. These narratives preserve what those shared experiences meant to the people who were there."
    />

    <main>
      <v-container>
        <section class="story-filters" aria-labelledby="story-filter-heading">
          <div>
            <p>Browse the Narratives</p>
            <h2 id="story-filter-heading">Find a Story</h2>
          </div>
          <div class="story-filter-list" role="group" aria-label="Filter stories by category">
            <button
              v-for="category in categories"
              :key="category"
              type="button"
              :class="{ active: activeCategory === category }"
              :aria-pressed="activeCategory === category"
              @click="activeCategory = category"
            >
              {{ category }}
            </button>
          </div>
        </section>

        <section v-if="featuredNarrative" class="stories-section stories-section--featured">
          <p class="kicker">Featured Narrative</p>
          <h2>Start Here</h2>
          <div class="featured-grid">
            <CultureCard v-bind="cardFor(featuredNarrative)" badge="Featured" />
          </div>
        </section>

        <section class="stories-section">
          <p class="kicker">Published Stories</p>
          <h2>{{ activeCategory === 'All' ? 'All Narratives' : activeCategory }}</h2>
          <div v-if="remainingNarratives.length" class="story-grid">
            <CultureCard
              v-for="story in remainingNarratives"
              :key="story.id"
              v-bind="cardFor(story)"
            />
          </div>
          <p v-else class="empty-state">No additional published stories are available in this category yet.</p>
        </section>
      </v-container>
    </main>
    <AppFooter />
  </div>
</template>

<script>
import AppFooter from '@/components/Footer.vue';
import CultureCard from '@/components/reckon-way/culture/CultureCard.vue';
import CultureHero from '@/components/reckon-way/culture/CultureHero.vue';
import CultureNav from '@/components/reckon-way/culture/CultureNav.vue';
import { narratives } from '@/content/culture/narratives';
import { visibleContent } from '@/lib/draftMode';

export default {
  name: 'CultureStoriesView',
  components: {
    AppFooter,
    CultureCard,
    CultureHero,
    CultureNav,
  },
  data: () => ({
    activeCategory: 'All',
    sourceNarratives: narratives,
  }),
  computed: {
    narratives() {
      return visibleContent(
        [...this.sourceNarratives, ...this.$store.getters.visibleRemoteContent.narratives],
        this.$store.getters.draftModeEnabled,
        this.$store.getters.isAuthorizedReckonUser,
      );
    },
    categories() {
      return ['All', ...new Set(this.narratives.map((story) => story.category))];
    },
    filteredNarratives() {
      if (this.activeCategory === 'All') return this.narratives;
      return this.narratives.filter((story) => story.category === this.activeCategory);
    },
    featuredNarrative() {
      return [...this.filteredNarratives]
        .filter((story) => story.featured)
        .sort((a, b) => a.featuredPriority - b.featuredPriority)[0] || null;
    },
    remainingNarratives() {
      return this.filteredNarratives.filter((story) => story.id !== this.featuredNarrative?.id);
    },
  },
  methods: {
    cardFor(story) {
      return {
        title: story.title,
        href: story.href,
        image: story.image,
        eyebrow: story.category,
        summary: story.excerpt,
        metadata: [story.source, story.dateLabel].filter(Boolean),
        ctaText: 'Read the Story',
        status: story.status,
      };
    },
  },
};
</script>

<style scoped>
.stories-page,
.stories-page main {
  background: #050505;
}

.stories-page main {
  padding: 48px 0 76px;
}

.story-filters {
  align-items: center;
  background: #111;
  border: 1px solid #303030;
  border-radius: 12px;
  display: flex;
  gap: 28px;
  justify-content: space-between;
  padding: 24px;
}

.story-filters p,
.kicker {
  color: #e23232;
  font-family: "Oswald", sans-serif;
  font-size: .78rem;
  font-weight: 900;
  margin: 0 0 5px;
  text-transform: uppercase;
}

.story-filters h2,
.stories-section h2 {
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: 2rem;
  margin: 0;
}

.story-filter-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.story-filter-list button {
  background: #1b1b1b;
  border: 1px solid #424242;
  border-radius: 999px;
  color: #ddd;
  font-family: "Oswald", sans-serif;
  font-size: .78rem;
  font-weight: 800;
  padding: 9px 16px;
  text-transform: uppercase;
}

.story-filter-list button:hover,
.story-filter-list button:focus,
.story-filter-list button.active {
  background: #e23232;
  border-color: #e23232;
  color: #fff;
}

.stories-section {
  border-top: 1px solid #292929;
  margin-top: 54px;
  padding-top: 48px;
}

.stories-section h2 {
  font-size: 2.5rem;
  margin-bottom: 24px;
}

.featured-grid {
  max-width: 760px;
}

.story-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
}

.empty-state {
  color: #aaa;
}

@media (max-width: 700px) {
  .story-filters {
    align-items: flex-start;
    flex-direction: column;
  }

  .story-filter-list {
    justify-content: flex-start;
  }
}
</style>
